import React, { useEffect, useState } from 'react';
import IPage from '../../interfaces/IPage';
import { Container, Form } from './InternalUserForm.styled';
import PageTitle from '../../components/page-title/PageTitle';
import { Backdrop, CircularProgress, Grid, IconButton, TextField, Tooltip } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { MdOutlineAssignmentReturn } from 'react-icons/md';
import IInternalUser from '../../interfaces/IInternalUser';
import * as yup from 'yup';
import IFormError from '../../interfaces/IFormError';
import { useToastrVanila } from '../../hooks/util/useToastrVanila';
import { useValidateSchema } from '../../hooks/providers/useValidateSchema';
import { useInternalUserService } from '../../hooks/services/useInternalUserService';
import { LoadingButton } from '@mui/lab';

const initialUser: IInternalUser = { userName: '', code: '', email: '', fullName: '' };

const InternalUserForm = (props: IPage) => {
  window.document.title = props.title;

  const [loading, setLoading] = useState<boolean>(false);
  const [user, setUser] = useState<IInternalUser>(initialUser);
  const [errors, setErrors] = useState<IFormError | null>(null);
  const [action, setAction] = useState<'edit' | 'new'>('edit');
  const [id, setId] = useState('');

  const userService = useInternalUserService();
  const navigate = useNavigate();
  const location = useLocation();
  const toastr = useToastrVanila();
  const validation = useValidateSchema();

  const schema = yup.object({
    userName: yup.string().required('Campo obrigatório'),
    code: yup.string().required('Campo obrigatório'),
    email: yup.string().required('Campo obrigatório').email('Informe um e-mail válido.'),
    fullName: yup.string().required('Campo obrigatório'),
  });

  const listUser = async () => {
    const id = location.pathname.split('/')[location.pathname.split('/').length - 2];
    setLoading(true);
    await userService
      .listById(id)
      .then(result => {
        result &&
          setUser({
            id: result.id,
            userName: result.userName,
            code: result.code,
            email: result.email,
            fullName: result.fullName,
          });
      })
      .catch(error => {
        toastr.error(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleCancel = async () => {
    if (action === 'edit') {
      listUser();
    } else {
      setUser({ ...initialUser });
    }
    setErrors(null);
  };

  const handleSaveClick = async () => {
    const result = await validation.validate(schema, user);
    setErrors(result.errors);
    if (!result.isValid) {
      return;
    }
    if (action === 'edit') {
      await update();
    } else {
      await create();
    }
  };

  const create = async (): Promise<void> => {
    setLoading(true);
    await userService
      .create({ ...user })
      .then(result => {
        setLoading(false);
        setUser({ ...result });
        toastr.success('Usuário cadastrado com sucesso!');
        navigate(`/user`);
      })
      .catch(error => {
        setLoading(false);
      });
  };

  const update = async (): Promise<void> => {
    setLoading(true);
    await userService
      .update(id, { ...user })
      .then(result => {
        setLoading(false);
        setUser({ ...result });
        toastr.success('Usuário atualizado com sucesso!');
        navigate(`/user`);
      })
      .catch(error => {
        setLoading(false);
      });
  };

  useEffect(() => {
    const id = location.pathname.split('/')[location.pathname.split('/').length - 2];
    const action = location.pathname.split('/')[location.pathname.split('/').length - 1] as 'edit' | 'new';
    setAction(action);

    setId(id);
    if (id !== '0') {
      setUser({ ...user, id });
      listUser();
    }
  }, []);

  return (
    <Container>
      <PageTitle title="CADASTRO DE  USUÁRIOS">
        <Tooltip title="Voltar" placement="left-start">
          <IconButton onClick={() => navigate(`/user`)} color="inherit">
            <MdOutlineAssignmentReturn />
          </IconButton>
        </Tooltip>
      </PageTitle>
      <Form>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Login Senior X"
              placeholder="Informe o login do usuário na Plataforma SeniorX"
              value={user.userName}
              onChange={e => setUser({ ...user, userName: e.target.value })}
              error={Boolean(errors?.userName)}
              helperText={errors?.userName}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Código do Usuário"
              placeholder="Informe código do usuário"
              value={user.code}
              onChange={e => setUser({ ...user, code: e.target.value })}
              error={Boolean(errors?.code)}
              helperText={errors?.code}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="E-mail"
              placeholder="Informe e-mail do usuário"
              value={user.email}
              onChange={e => setUser({ ...user, email: e.target.value })}
              error={Boolean(errors?.email)}
              helperText={errors?.email}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Nome completo"
              placeholder="Informe nome completo usuário"
              value={user.fullName}
              onChange={e => setUser({ ...user, fullName: e.target.value })}
              error={Boolean(errors?.fullName)}
              helperText={errors?.fullName}
            />
          </Grid>
        </Grid>
      </Form>
      <Grid container direction="row" justifyContent="flex-start" alignItems="center" sx={{ marginTop: 5 }} spacing={2}>
        <Grid item>
          <LoadingButton loading={loading} variant="contained" color="secondary" onClick={handleCancel}>
            Cancelar
          </LoadingButton>
        </Grid>
        <Grid item>
          <LoadingButton loading={loading} variant="contained" color="primary" onClick={handleSaveClick}>
            Salvar
          </LoadingButton>
        </Grid>
      </Grid>
      <Backdrop sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Container>
  );
};

export default InternalUserForm;
